import { useCallback, useEffect, useRef, useState } from "react";
import { musicController } from "@/lib/audio/musicController";
import {
  getCachedLumiUrl,
  resolveLumiUrl,
  setCachedLumiUrl,
} from "@/lib/audio/lumiAudioCache";
import {
  lumiPlayback,
  type LumiStatus,
} from "@/lib/audio/lumiPlaybackController";

export const LUMI_VOICE_ID = "KR1TkIhkSykEjI4B0DtH";
export const LUMI_MAX_CHARS = 600;

type Status = LumiStatus | "generating" | "ready";

interface PlayOptions {
  /** Optional lesson/context id, forwarded to the edge function for cache scoping. */
  contextId?: string | null;
  /** Allow learner-initiated live TTS generation for approved/static or verified Explore summaries. */
  allowGenerate?: boolean;
  /** Dedicated verified Explore path. Never sends freeform text as source-of-truth. */
  mode?: "static" | "explore-summary";
  summaryId?: string | null;
}

function trim(text: string): string {
  const clean = (text || "").trim().replace(/\s+/g, " ");
  if (clean.length <= LUMI_MAX_CHARS) return clean;
  // Cut at the last sentence/word boundary within the cap.
  const slice = clean.slice(0, LUMI_MAX_CHARS);
  const lastStop = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("! "), slice.lastIndexOf("? "));
  if (lastStop > LUMI_MAX_CHARS * 0.6) return slice.slice(0, lastStop + 1);
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice) + "…";
}

/**
 * Lumi Vanta voice playback. Requires a user gesture to start.
 *
 * Playback priority per tap:
 *  1) If a resolved ElevenLabs URL is already cached → play it synchronously.
 *  2) Otherwise, immediately speak via Web Speech API inside the gesture
 *     (so iOS Safari / Chrome iOS actually vocalize) AND kick off a
 *     background URL fetch so subsequent taps upgrade to the ElevenLabs
 *     asset.
 *  3) If neither path is available, surface an "unavailable" state so the
 *     UI can communicate it via aria + icon.
 */
export function useLumiVoice() {
  const [globalStatus, setGlobalStatus] = useState<Status>(lumiPlayback.getStatus());
  const [isOwner, setIsOwner] = useState(false);
  const unduckRef = useRef<((ms?: number) => void) | null>(null);
  const activeRef = useRef(false); // does *this* hook instance own current playback
  const fetchingRef = useRef(false);

  const releaseDuck = useCallback(() => {
    const fn = unduckRef.current;
    unduckRef.current = null;
    if (fn) fn(400);
  }, []);

  const acquireDuck = useCallback(() => {
    if (unduckRef.current) return;
    unduckRef.current = musicController.pushDuck(0.4, 250);
  }, []);

  // Subscribe to the singleton. Only the hook instance that *owns* the
  // current playback surfaces "playing"/"loading" to its consumer — every
  // other mounted button shows idle so the UI clearly identifies which
  // button is actually playing and non-owner clicks read as "Play" (they
  // will swap to a new clip), not "Pause".
  useEffect(() => {
    return lumiPlayback.subscribe((next) => {
      setGlobalStatus(next);
      if (next !== "playing" && next !== "loading") {
        if (activeRef.current) {
          activeRef.current = false;
          setIsOwner(false);
          releaseDuck();
        }
      }
    });
  }, [releaseDuck]);

  useEffect(() => {
    return () => {
      if (activeRef.current) {
        lumiPlayback.stop();
        releaseDuck();
      }
    };
  }, [releaseDuck]);

  const stop = useCallback(() => {
    lumiPlayback.stop();
    activeRef.current = false;
    setIsOwner(false);
    releaseDuck();
  }, [releaseDuck]);

  // Synchronous entry so iOS/WebKit accepts the audio + speech calls as
  // originating from the user gesture.
  //
  // Priority (per user requirement — ElevenLabs must win when available):
  //  1) Cached ElevenLabs URL → play synchronously.
  //  2) `allowGenerate` set (approved static line OR verified explore
  //     summary) → mark loading, fetch/generate the ElevenLabs asset, then
  //     play it. Only if the fetch returns `missing` or the audio element
  //     rejects do we fall back to Web Speech.
  //  3) Freeform/unapproved text → the server will not generate. Use Web
  //     Speech synthesis as the intentional fallback.
  const play = useCallback((rawText: string, opts: PlayOptions = {}) => {
    const text = trim(rawText);
    if (!text) return;

    // Toggle off if we're already playing.
    if (activeRef.current && lumiPlayback.getStatus() === "playing") {
      stop();
      return;
    }

    const mode = opts.mode ?? "static";
    const canGenerate = opts.allowGenerate === true;
    const cacheKey = mode === "explore-summary"
      ? `explore-summary:${opts.contextId ?? "none"}:${opts.summaryId ?? "none"}`
      : text;
    const cached = getCachedLumiUrl(cacheKey);

    // NEVER fall back to browser speech synthesis. If the official
    // ElevenLabs asset for Lumi Vanta is unavailable, surface an
    // `unavailable` state so the UI can communicate it — do not
    // silently vocalize with a system voice pretending to be Lumi.
    const markUnavailable = (reason: string) => {
      if (import.meta.env.DEV) {
        console.info("[lumi-voice] ElevenLabs unavailable — no fallback", {
          reason,
          mode,
          voiceId: "KR1TkIhkSykEjI4B0DtH",
        });
      }
      lumiPlayback.markUnavailable();
      return false;
    };

    // Priority 1: cached ElevenLabs asset — synchronous play (iOS-safe).
    if (cached) {
      if (import.meta.env.DEV) {
        console.info("[lumi-voice] playing cached ElevenLabs asset", {
          mode,
          hasUrl: true,
          source: "cached_asset",
          voiceId: "KR1TkIhkSykEjI4B0DtH",
        });
      }
      const started = lumiPlayback.playAudioUrl(cached);
      if (started) {
        activeRef.current = true;
        setIsOwner(true);
        acquireDuck();
        return;
      }
      // Cached URL failed to start — fall through to fetch.
    }

    // Priority 2: try to fetch/generate ElevenLabs first when the line is
    // eligible (approved static or verified explore summary). If the
    // server returns `missing` or audio playback fails we surface an
    // unavailable state — we do NOT fall back to browser speech.
    if (canGenerate && !fetchingRef.current) {
      fetchingRef.current = true;
      lumiPlayback.markLoading();
      activeRef.current = true;
      setIsOwner(true);
      acquireDuck();
      if (import.meta.env.DEV) {
        console.info("[lumi-voice] resolving ElevenLabs URL", {
          mode,
          contextId: opts.contextId,
          speaker: "lumi",
          character: "Lumi Vanta",
          voiceId: "KR1TkIhkSykEjI4B0DtH",
        });
      }
      resolveLumiUrl(text, opts.contextId ?? null, {
        allowGenerate: true,
        mode,
        summaryId: opts.summaryId ?? null,
      })
        .then((result) => {
          if (import.meta.env.DEV) {
            console.info("[lumi-voice] ElevenLabs resolve result", {
              mode,
              hasUrl: !!result.url,
              cached: result.cached,
              generated: result.generated,
              missing: result.missing,
              reason: result.reason,
            });
          }
          if (result.url) {
            setCachedLumiUrl(cacheKey, result.url);
            const started = lumiPlayback.playAudioUrl(result.url);
            if (!started) markUnavailable("audio_element_rejected");
            return;
          }
          markUnavailable(result.reason ?? "no_elevenlabs_url");
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.warn("[lumi-voice] ElevenLabs resolve failed", err);
          }
          markUnavailable("resolve_exception");
        })
        .finally(() => {
          fetchingRef.current = false;
        });
      return;
    }

    // Priority 3: freeform / unapproved line and no cached asset.
    // Per HeirWise voice-identity rules we do not vocalize Lumi lines
    // with browser speech synthesis. Mark as unavailable so the UI
    // can degrade cleanly instead of using a random system voice.
    markUnavailable("not_eligible_for_generation");
  }, [stop, acquireDuck]);

  // Per-instance status: only surface active playback state to the owner.
  // Non-owners see idle (or the shared unavailable/failed terminal states,
  // which are safe to reflect globally since they don't imply "this button
  // is playing").
  const status: Status = isOwner
    ? globalStatus
    : globalStatus === "unavailable" || globalStatus === "failed"
      ? "idle"
      : "idle";

  return { status, play, stop };
}